import React, { useRef, useState } from "react";
import styles from "./ContactForm.module.scss";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_SITE_KEY = "6LcobusrAAAAANH9H7-CiqugwqaggkcjNMFF7ByW"; // або process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY

const PartnerForm: React.FC = () => {
  const [formData, setFormData] = useState({
    companyName: "",
    email: "",
    postalCode: "",
    city: "",
    phone: "",
    contactName: "",
    companyType: "",
    services: {
      diagnosis: false,
      partsExchange: false,
      repairs: false,
      maintenance: false,
      others: false,
    },
    message: "",
    consent: false,
  });

  // ---- reCAPTCHA
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Обробка змін у полях форми
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox" && "checked" in e.target) {
      const { checked } = e.target as HTMLInputElement;
      if (name in formData.services) {
        setFormData((prevData) => ({
          ...prevData,
          services: {
            ...prevData.services,
            [name]: checked,
          },
        }));
      } else {
        setFormData((prevData) => ({
          ...prevData,
          [name]: checked,
        }));
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  // Обробка відправки форми
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!captchaToken) {
      alert("Bitte bestätigen Sie, dass Sie kein Roboter sind."); // підтвердіть, що ви не робот
      return;
    }

    const formPayload = {
      type: "feedback",
      feedbacktype: "feedbackpartners",
      legid: 1, // Відповідний ID для партнера
      data: {
        companyName: formData.companyName,
        email: formData.email,
        postalCode: formData.postalCode,
        city: formData.city,
        phone: formData.phone,
        contactName: formData.contactName,
        companyType: formData.companyType,
        services: formData.services,
        message: formData.message,
        recaptchaToken: captchaToken, // <- ДОДАНО: токен капчі (валідуйте на бекенді)
      },
    };

    try {
      setLoading(true);
      const response = await sendToCRM(formPayload); // Відправка в CRM
      console.log("CRM Response:", response);
      alert("Ihre Anfrage wurde erfolgreich gesendet!");

      // Аналітика (у тебе тут була назва 'career_form' — логічніше назвати partner_form)
      window.dataLayer?.push({
        event: "form_submit",
        form_name: "partner_form",
        form_type: "feedbackpartners",
      });

      // Очищення форми
      setFormData({
        companyName: "",
        email: "",
        postalCode: "",
        city: "",
        phone: "",
        contactName: "",
        companyType: "",
        services: {
          diagnosis: false,
          partsExchange: false,
          repairs: false,
          maintenance: false,
          others: false,
        },
        message: "",
        consent: false,
      });

      // Скинути капчу
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } catch (error) {
      console.error("Fehler beim Senden der Anfrage", error);
      alert("Etwas ist schief gelaufen. Versuchen Sie es später erneut.");
    } finally {
      setLoading(false);
    }
  };

  // Відправка даних у CRM
  const sendToCRM = async (data: any) => {
    const res = await fetch("https://crm.hixa.eu/external/goodsapi/", {
      method: "POST",
      headers: {
        Authorization: "Bearer DH345DFGDGb32FDsf03876SDFSdf13",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        Accept: "application/json",
      },
      body: new URLSearchParams({
        type: data.type,
        legid: String(data.legid),
        feedbacktype: data.feedbacktype,
        data: JSON.stringify(data.data), // Відправляємо як JSON рядок
      }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Failed to send message to CRM: ${res.status} ${text}`);
    }
    return res.json();
  };

  return (
    <div className={`container ${styles.contactSection}`}>
      <div className={`sideP88 sectionContainerWhite ${styles.formContainer}`}>
        <div className={styles.contactSectionBlock}>
          <div className={styles.sectionDesk}>
            <h3 className={styles.heading}>
              Kontaktieren Sie uns{" "}
              <div>und werden Sie Teil unseres Partnernetzwerks!</div>
            </h3>
            <p className={styles.subheading}>
              Mit HIXA GmbH optimieren Sie Ihre Werkstattprozesse und steigern
              Ihre Effizienz.
            </p>
          </div>

          <form className={styles.feedbackForm} onSubmit={handleSubmit}>
            <div className={styles.doubleInput}>
              <div className={styles.formGroup}>
                <label htmlFor="companyName" className={styles.label}>
                  Name des Unternehmens *
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  placeholder="Company GmbH *"
                  value={formData.companyName}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="contactName" className={styles.label}>
                  Name des Kontaktperson *
                </label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  placeholder="Max Mustermann *"
                  value={formData.contactName}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.doubleInput}>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  E-Mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="max.mustermann@mail.com *"
                  value={formData.email}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="phone" className={styles.label}>
                  Telefon *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="+49 00 000 0000 *"
                  value={formData.phone}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.doubleInput}>
              <div className={`${styles.formGroup} ${styles.w32}`}>
                <label htmlFor="postalCode" className={styles.label}>
                  PLZ *
                </label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  placeholder="13089 *"
                  value={formData.postalCode}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={`${styles.formGroup} ${styles.w64}`}>
                <label htmlFor="city" className={styles.label}>
                  Stadt *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  placeholder="Berlin *"
                  value={formData.city}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={`${styles.formGroup} ${styles.pb20}`}>
              <label htmlFor="companyType" className={styles.label}>
                Art des Unternehmens *
              </label>
              <select
                id="companyType"
                name="companyType"
                value={formData.companyType}
                onChange={handleChange}
                className={styles.select}
                required
              >
                <option value="">Wählen Sie...</option>
                <option value="company1">Werkstatt</option>
                <option value="company2">Autohaus</option>
                <option value="company3">Fuhrparkmanagement</option>
                <option value="company4">Sonstiges</option>
              </select>
            </div>

            <div className={`${styles.formGroup} ${styles.pb20}`}>
              <label className={styles.label}>
                Dienstleistungen von Interesse
              </label>
              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="diagnosis"
                    checked={formData.services.diagnosis}
                    onChange={handleChange}
                  />
                  Diagnose
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="repairs"
                    checked={formData.services.repairs}
                    onChange={handleChange}
                  />
                  Reparaturen
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="partsExchange"
                    checked={formData.services.partsExchange}
                    onChange={handleChange}
                  />
                  Austausch von Bauteilen
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="maintenance"
                    checked={formData.services.maintenance}
                    onChange={handleChange}
                  />
                  Wartung
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="others"
                    checked={formData.services.others}
                    onChange={handleChange}
                  />
                  Sonstige
                </label>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="message" className={styles.label}>
                Ihre Nachricht *
              </label>
              <textarea
                id="message"
                name="message"
                placeholder="Ihre Nachricht an uns *"
                value={formData.message}
                onChange={handleChange}
                className={styles.textarea}
                required
              ></textarea>
            </div>

            <div className={styles.consent}>
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                className={styles.checkbox}
                required
              />
              <label htmlFor="consent">
                Ich bin damit einverstanden, dass meine personenbezogenen Daten
                gemäß der Datenschutz-Grundverordnung (DSGVO) von der HIXA GmbH,
                Neukirchstraße 74, 13089 Berlin, zum Zweck der Bearbeitung
                meiner Anfrage verarbeitet und gespeichert werden. Weitere
                Informationen finde ich in der{" "}
                <a
                  href="/datenschutzerklarung/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Datenschutzerklärung
                </a>
                .
              </label>
            </div>

            {/* reCAPTCHA */}
            <div className={styles.formGroup}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />
            </div>

            <button
              type="submit"
              className={`commonButton buttonMd ${styles.submitButton}`}
              disabled={loading || !formData.consent || !captchaToken}
            >
              {loading ? "Senden…" : "Absenden"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PartnerForm;
